import { CheckCircle } from "lucide-react";

import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { HubLinkListCopy } from "@components/bluetooth-landing/HubLinkList";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";

export const title =
  "Bluetooth Low Energy - firmware BLE, aplikacje mobilne i urządzenia connected";

export const description =
  "Firma BLE: firmware Bluetooth Low Energy, aplikacje mobilne i urządzenia connected. Projektujemy oprogramowanie BLE na Nordic nRF oraz aplikacje iOS i Android do komunikacji z urządzeniem.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Blues Brackets",
    url: "https://bluesbrackets.com",
    logo: "https://bluesbrackets.com/favicon-dark.png",
    description,
    areaServed: "EU",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "hello@bluesbrackets.com",
      availableLanguage: ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Usługi Bluetooth Low Energy",
    provider: {
      "@type": "Organization",
      name: "Blues Brackets",
    },
    description,
    areaServed: "EU",
    serviceType: "Bluetooth Low Energy development",
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Usługi Bluetooth Low Energy",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Aplikacja BLE - komunikacja z urządzeniem Bluetooth Low Energy",
        url: "https://bluesbrackets.com/aplikacje-bluetooth",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Firmware BLE - programowanie urządzeń Bluetooth Low Energy",
        url: "https://bluesbrackets.com/programowanie-firmware-ble",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Prototypy urządzeń BLE - wearable, beacony i audio",
        url: "https://bluesbrackets.com/prototypy-urzadzen-ble",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Programowanie Nordic nRF52, nRF53, nRF54",
        url: "https://bluesbrackets.com/programowanie-nordic-nrf",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Artykuły Bluetooth Low Energy",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Co to jest Bluetooth Low Energy i czym różni się od Bluetooth Classic?",
        url: "https://bluesbrackets.com/pl/blogs/co-to-jest-bluetooth-low-energy-ble",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Nordic nRF52832 vs nRF52840 vs nRF5340 vs nRF54",
        url: "https://bluesbrackets.com/pl/blogs/nordic-nrf52832-vs-nrf52840-vs-nrf5340-vs-nrf54",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Aplikacja mobilna do urządzenia od producenta OEM",
        url: "https://bluesbrackets.com/pl/blogs/aplikacja-mobilna-do-urzadzenia-od-producenta-oem",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Ile kosztuje aplikacja BLE?",
        url: "https://bluesbrackets.com/pl/blogs/ile-kosztuje-aplikacja-ble",
      },
    ],
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Baza wiedzy",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Wyceń projekt",
};

export const heroCopy: HeroCopy = {
  header: "Bluetooth Low Energy",
  headerTail: "dla urządzeń connected",
  description:
    "Projektujemy firmware BLE, aplikacje mobilne i prototypy urządzeń Bluetooth Low Energy. Polski zespół dla firm z urządzeniem, które ma działać stabilnie na rynku UE.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Zobacz usługi BLE",
    href: "#uslugi",
  },
  benefits: [
    { text: "Firmware i aplikacje BLE", Icon: CheckCircle },
    { text: "Nordic nRF52 / nRF53 / nRF54", Icon: CheckCircle },
    { text: "Gotowe na RODO i rynek UE", Icon: CheckCircle },
  ],
};

export const introCopy = {
  id: "dla-kogo",
  eyebrow: "Dla kogo",
  heading: "Komercyjny partner BLE dla firm z urządzeniem Bluetooth",
  paragraphs: [
    "Jeśli masz lub planujesz urządzenie z Bluetooth Low Energy i potrzebujesz firmware, aplikacji mobilnej albo prototypu — jesteśmy zespołem, który łączy te warstwy w jeden produkt.",
    "Pracujemy z producentami, importerami elektroniki i startupami hardware. Od chipa Nordic nRF, przez profil GATT i OTA, po aplikację iOS/Android gotową do sklepów.",
  ],
};

export const servicesListCopy: HubLinkListCopy = {
  id: "uslugi",
  eyebrow: "Usługi BLE",
  heading: "Landingi usługowe w klastrze Bluetooth Low Energy",
  intro:
    "Wybierz obszar, który najlepiej opisuje Twój projekt — każda strona zbiera typowe scenariusze, zakres prac i sposób współpracy przy produktach BLE.",
  items: [
    {
      href: "/aplikacje-bluetooth",
      title: "Aplikacja BLE - komunikacja z urządzeniem Bluetooth Low Energy",
      description:
        "Aplikacja mobilna BLE na iOS i Android: parowanie, GATT, powiadomienia, OTA i UX pod urządzenie Bluetooth Low Energy.",
    },
    {
      href: "/programowanie-firmware-ble",
      title: "Firmware BLE - programowanie urządzeń Bluetooth Low Energy",
      description:
        "Programowanie firmware BLE: stack Bluetooth Low Energy, profile GATT, zużycie energii, DFU/OTA i przygotowanie do produkcji.",
    },
    {
      href: "/prototypy-urzadzen-ble",
      title: "Prototypy urządzeń BLE - wearable, beacony i audio",
      description:
        "Prototypy urządzeń BLE — wearable, beacony i Bluetooth LE Audio. Szybka walidacja pomysłu przed produkcją seryjną.",
    },
    {
      href: "/programowanie-nordic-nrf",
      title:
        "Programowanie Nordic nRF52, nRF53, nRF54 - specjaliści od sprzętu Nordic Semiconductor",
      description:
        "Firmware na Nordic nRF52832/nRF52840, nRF5340, nRF54 i Thingy — nRF Connect SDK, Zephyr RTOS i towarzysząca aplikacja mobilna.",
    },
  ],
};

export const blogListCopy: HubLinkListCopy = {
  id: "wartosci",
  eyebrow: "Baza wiedzy",
  heading: "Artykuły wspierające klaster Bluetooth Low Energy",
  intro:
    "Praktyczne teksty o BLE, doborze chipów Nordic i planowaniu aplikacji do urządzenia — od podstaw technologii po wycenę projektu.",
  items: [
    {
      href: "/pl/blogs/co-to-jest-bluetooth-low-energy-ble",
      title:
        "Co to jest Bluetooth Low Energy i czym różni się od Bluetooth Classic?",
      description:
        "Co to jest BLE, czym różni się od Bluetooth Classic i kiedy Bluetooth Low Energy ma sens w produkcie connected.",
    },
    {
      href: "/pl/blogs/nordic-nrf52832-vs-nrf52840-vs-nrf5340-vs-nrf54",
      title:
        "Nordic nRF52832 vs Nordic nRF52840 vs Nordic nRF5340 vs Nordic nRF54 — jaki mikrokontroler wybrać?",
      description:
        "Porównanie Nordic nRF52832, nRF52840, nRF5340 i nRF54 — który chip wybrać do nowego projektu BLE.",
    },
    {
      href: "/pl/blogs/aplikacja-mobilna-do-urzadzenia-od-producenta-oem",
      title:
        "Aplikacja mobilna do urządzenia od producenta OEM (np. z Chin) - jak to zaplanować?",
      description:
        "Integracja z producentem OEM/ODM, white-label hardware i aplikacja mobilna pod własną markę na rynku PL/EU.",
    },
    {
      href: "/pl/blogs/ile-kosztuje-aplikacja-ble",
      title: "Ile kosztuje aplikacja BLE? Wycena projektu Bluetooth Low Energy",
      description:
        "Ile kosztuje aplikacja BLE — widełki wyceny i czynniki wpływające na koszt projektu Bluetooth Low Energy.",
    },
  ],
};

export const processCopy = {
  id: "proces",
  eyebrow: "Proces",
  heading: "Od konsultacji do działającego produktu BLE",
  steps: [
    {
      title: "Konsultacja i zakres",
      description:
        "Omawiamy urządzenie, cele biznesowe i ryzyka techniczne — firmware, aplikacja, chip, dokumentacja OEM.",
    },
    {
      title: "Prototyp i integracja",
      description:
        "Budujemy działający fragment produktu: komunikację BLE, kluczowe scenariusze użytkownika i testy na sprzęcie.",
    },
    {
      title: "Wdrożenie i utrzymanie",
      description:
        "Doprowadzamy firmware i aplikację do produkcji, publikacji w sklepach oraz wsparcia po premierze.",
    },
  ],
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Masz urządzenie z Bluetooth Low Energy?",
    p: "Umów się na darmową konsultację — opowiedz o produkcie, a zaproponujemy zakres firmware, aplikacji lub prototypu BLE.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Jesteśmy firmą BLE: firmware Bluetooth Low Energy, aplikacje mobilne i urządzenia connected. Działamy z Polski, obsługujemy klientów w UE.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Usługi BLE" },
      { href: "#wartosci", text: "Baza wiedzy" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "/aplikacje-bluetooth", text: "Aplikacje Bluetooth" },
      { href: "/programowanie-firmware-ble", text: "Firmware BLE" },
      { href: "/prototypy-urzadzen-ble", text: "Prototypy urządzeń BLE" },
      { href: "/programowanie-nordic-nrf", text: "Programowanie Nordic nRF" },
      { href: "/iot", text: "Systemy IoT" },
      { href: "/blog", text: "Blog" },
      { href: "/projects", text: "Projekty" },
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy BLE",
    links: [
      {
        href: "/pl/blogs/co-to-jest-bluetooth-low-energy-ble",
        text: "Co to jest Bluetooth Low Energy i czym różni się od Bluetooth Classic?",
      },
      {
        href: "/pl/blogs/nordic-nrf52832-vs-nrf52840-vs-nrf5340-vs-nrf54",
        text: "Nordic nRF52832 vs nRF52840 vs nRF5340 vs nRF54 — jaki chip wybrać?",
      },
      {
        href: "/pl/blogs/aplikacja-mobilna-do-urzadzenia-od-producenta-oem",
        text: "Aplikacja mobilna do urządzenia od producenta OEM",
      },
      {
        href: "/pl/blogs/ile-kosztuje-aplikacja-ble",
        text: "Ile kosztuje aplikacja BLE?",
      },
    ],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: {
    heading: "Napisz do nas",
    fields: {
      name: {
        label: "Imię i nazwisko",
        placeholder: "Jan Kowalski",
        requiredError: "Podaj imię i nazwisko",
      },
      email: {
        label: "Adres e-mail",
        placeholder: "jan@firma.pl",
        requiredError: "Podaj adres e-mail",
        invalidError: "Podaj poprawny adres e-mail",
      },
      topic: {
        label: "Opis projektu",
        placeholder: "Krótko opisz swoje urządzenie BLE i potrzeby...",
        requiredError: "Opisz swój projekt",
        maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
      },
    },
    submit: "Wyślij zapytanie",
    submitting: "Wysyłanie...",
    successMessage: "Wiadomość wysłana!",
    errorMessage: "Nie udało się wysłać wiadomości :(",
    responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
    emailSubjectPrefix: "Imię i nazwisko",
  },
};
